import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  CircularProgress,
  IconButton,
  Box,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArrowBackIosNew from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";
import { ReactComponent as Export } from "../assets/icons/export.svg";

import {
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { isValid, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

import { getOperatorCountryNameOptions } from "../services/CountryService";
import {
  fetchMessageTrackersDialog,
  MessageTrackerType,
} from "../services/MessageTrackerService";
import CdrExportDialog from "./CdrExportDialog";
import { Params } from "../types/table/interfaces.type";
import { IField } from "../types/table/interfaces.type";
import { COLORS } from "../assets/theme";
import { getCustomerOptions } from "../services/ClientService";
import { getOperatorNameOptions } from "../services/OperatorsService";
import { getProviderOptions } from "../services/ProviderService";
import CustomButton from "./common/CustomButton";
import { getOperatorIdByName } from "../services/OperatorsService";
import { connect } from "tls";
import { getAccountOptions } from "../services/AccountService";
import CopyableText from "./CopiedText";
import CopyableTextWithDialog from "./CopyableTextWithDialog";
import {
  getServiceProviderNameOptions,
  getServiceProviderCurrencyOptions,
} from "../services/ServiceProviderService";
import {
  getProviderCurrencyOptions,
  getConnectionProfileCurrencyOptions,
} from "../services/CurrencyService";
import CostBreakdownWithDialog from "./CostBreakdownWithDialog";
import ProviderWithFallback from "./ProviderWithFallback";
import useDragScroll from "../utils/useDragScroll";
interface ExtendedParams extends Params<MessageTrackerType> {
  connectionProfileId?: number;
  vendorId?: number;
  destinationOperatorId?: number;
  type?: string;
}
const iso8601DateTimeRegex =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+\-]\d{2}:\d{2})?$/;

function isValidDate(dateString: string): boolean {
  if (!iso8601DateTimeRegex.test(dateString)) return false;
  const parsed = parseISO(dateString);
  return isValid(parsed);
}

function formatDateString(dateString: string): string {
  const parsedDate = parseISO(dateString);
  if (!isValid(parsedDate)) return dateString;
  return formatInTimeZone(parsedDate, "UTC", "dd-MM-yyyy HH:mm:ss a");
}

const formatValue = (val: any): string => {
  if (val === undefined || val === null) {
    return "-";
  }
  if (val instanceof Date) {
    return formatInTimeZone(val, "UTC", "dd-MM-yyyy HH:mm:ss");
  }
  if (typeof val === "string" && isValidDate(val)) {
    return formatDateString(val);
  }
  if (typeof val === "object") {
    return JSON.stringify(val, null, 2);
  }
  return String(val);
};

interface MessageTrackersDialogProps {
  open: boolean;
  onClose: () => void;
  params: Params<MessageTrackerType>;
  usedBy?: "live-monitor" | "repricing";
}

export const MessageTrackersDialog: React.FC<MessageTrackersDialogProps> = ({
  open,
  onClose,
  params,
  usedBy,
}) => {
  const extendedParams = params as ExtendedParams;
  const queryClient = useQueryClient();
  const [pageInput, setPageInput] = useState({ page: 0, size: 20 });
  const [pageable, setPageable] = useState<{
    content: MessageTrackerType[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
  }>({ content: [], totalElements: 0, totalPages: 0, number: 0, size: 20 });
  const [loading, setLoading] = useState(false);
  const [cdrExportDialogOpen, setCdrExportDialogOpen] = useState(false);
  const [highlightedRowId, setHighlightedRowId] = useState<string | number | null>(
    null,
  );
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const dragScrollRef = useDragScroll<HTMLDivElement>();

  const fields = useMemo<IField<MessageTrackerType>[]>(
    () => [
      {
        key: "messageId",
        header: "Message ID",
        transform: async (row) => (
          <span
            style={{
              textDecoration: row.isConcatenated ? "underline" : "none",
            }}
          >
            {row.messageId}
          </span>
        ),
      },
      {
        key: "connectionProfileId",
        header: "Customer",
        options: (t) => getAccountOptions(String(t.connectionProfileId)),
      },
      {
        key: "vendorId",
        header: "Provider",
        transform: async (row) => {
          const [providerName, fallbackOfProviderName] = await Promise.all([
            row.vendorId
              ? queryClient
                  .fetchQuery(getProviderOptions(String(row.vendorId)))
                  .catch(() => undefined)
              : Promise.resolve(undefined),
            row.fallbackOfVendorId
              ? queryClient
                  .fetchQuery(getProviderOptions(String(row.fallbackOfVendorId)))
                  .catch(() => undefined)
              : Promise.resolve(undefined),
          ]);
          return (
            <ProviderWithFallback
              providerName={providerName}
              fallbackOfProviderName={fallbackOfProviderName}
            />
          );
        },
      },
      { key: "status", header: "Status" },
      { key: "deliveryStat", header: "Delivery Status" },
      {
        key: "rate",
        header: "Rate",
        transform: async (row) =>
          row.ratePerDelivery && row.deliveryStat !== "DELIVRD"
            ? "-"
            : row.rate,
      },
      {
        key: "routingCost",
        header: "Total Cost",
        transform: async (row) => {
          const hideRoutingCost =
            row.costPerDelivery && row.deliveryStat !== "DELIVRD";
          const [
            mnpProviderName,
            hlrProviderName,
            routingCostCurrency,
            mnpCostCurrency,
            hlrCostCurrency,
            convertedCostCurrency,
          ] = await Promise.all([
            row.mnpProviderId && row.mnpCost > 0
              ? queryClient
                  .fetchQuery(getServiceProviderNameOptions(row.mnpProviderId))
                  .catch(() => undefined)
              : Promise.resolve(undefined),
            row.hlrProviderId && row.hlrCost > 0
              ? queryClient
                  .fetchQuery(getServiceProviderNameOptions(row.hlrProviderId))
                  .catch(() => undefined)
              : Promise.resolve(undefined),
            row.vendorId
              ? queryClient
                  .fetchQuery(getProviderCurrencyOptions(String(row.vendorId)))
                  .catch(() => undefined)
              : Promise.resolve(undefined),
            row.mnpProviderId && row.mnpCost > 0
              ? queryClient
                  .fetchQuery(
                    getServiceProviderCurrencyOptions(row.mnpProviderId),
                  )
                  .catch(() => undefined)
              : Promise.resolve(undefined),
            row.hlrProviderId && row.hlrCost > 0
              ? queryClient
                  .fetchQuery(
                    getServiceProviderCurrencyOptions(row.hlrProviderId),
                  )
                  .catch(() => undefined)
              : Promise.resolve(undefined),
            row.connectionProfileId
              ? queryClient
                  .fetchQuery(
                    getConnectionProfileCurrencyOptions(
                      row.connectionProfileId,
                    ),
                  )
                  .catch(() => undefined)
              : Promise.resolve(undefined),
          ]);

          return (
            <CostBreakdownWithDialog
              {...(hideRoutingCost
                ? {}
                : { routingCost: row.routingCost || 0 })}
              {...(row.mnpCost !== undefined && row.mnpCost !== null
                ? { mnpCost: row.mnpCost }
                : {})}
              {...(row.hlrCost !== undefined && row.hlrCost !== null
                ? { hlrCost: row.hlrCost }
                : {})}
              mnpProviderName={mnpProviderName}
              hlrProviderName={hlrProviderName}
              failureCause={row.failureCause}
              routingCostCurrency={routingCostCurrency}
              mnpCostCurrency={mnpCostCurrency}
              hlrCostCurrency={hlrCostCurrency}
              convertedCostCurrency={convertedCostCurrency}
              {...(!hideRoutingCost &&
              row.convertedRoutingCost !== undefined &&
              row.convertedRoutingCost !== null
                ? { convertedRoutingCost: row.convertedRoutingCost }
                : {})}
              {...(row.convertedMnpCost !== undefined &&
              row.convertedMnpCost !== null
                ? { convertedMnpCost: row.convertedMnpCost }
                : {})}
              {...(row.convertedHlrCost !== undefined &&
              row.convertedHlrCost !== null
                ? { convertedHlrCost: row.convertedHlrCost }
                : {})}
            />
          );
        },
      },
      { key: "originalSource", header: "Sender ID" },
      { key: "postTranslatedSource", header: "Translated Sender ID" },
      {
        key: "originalMessageText",
        header: "Content",
        transform: async (row) => (
          <CopyableTextWithDialog
            text={row.originalMessageText}
            header={
              row.isConcatenated
                ? "Full Message Content"
                : "Original Message Content"
            }
            concatenatedFullText={row.concatenatedFullText}
            isConcatenated={row.isConcatenated}
          />
        ),
      },
      {
        key: "messageText",
        header: "Translated Content",
        transform: async (row) => (
          <CopyableTextWithDialog
            text={row.messageText}
            header="Translated Message Content"
            concatenatedFullText={row.concatenatedFullText}
            isConcatenated={row.isConcatenated}
          />
        ),
      },
      { key: "postTranslatedDestination", header: "Mobile Number" },
      {
        key: "destinationOperatorId",
        header: "Country",
        options: (t) => getOperatorCountryNameOptions(t.destinationOperatorId),
      },
      {
        key: "destinationOperatorId",
        header: "Operator",
        options: (t) => getOperatorNameOptions(t.destinationOperatorId),
      },
      {
        key: "prefixMatchedOperatorId",
        header: "Ported From",
        options: (t) => getOperatorNameOptions(t.prefixMatchedOperatorId),
      },
      { key: "terminatorMessageId", header: "Provider Message ID" },
      { key: "ip", header: "IP" },
      { key: "dateReceived", header: "Date Received" },
      { key: "dateSent", header: "Date Sent" },
      { key: "dateDlrReceivedFromVendor", header: "Date DLR" },
      { key: "tps", header: "TPS" },
      {
        key: "dateSubmitSmResponse",
        header: "Delay to submitResp",
        transform: async (row) => {
          if (row.dateReceived && row.dateSubmitSmResponse) {
            const received = new Date(row.dateReceived);
            const submitResp = new Date(row.dateSubmitSmResponse);
            const diffMs = submitResp.getTime() - received.getTime();
            if (!isNaN(diffMs) && diffMs >= 0) {
              const seconds = diffMs / 1000;
              return `${seconds.toFixed(3)}s`;
            }
          }
          return "-";
        },
      },
      {
        key: "lastModified",
        header: "Delay To Submit",
        transform: async (row) => {
          if (row.dateReceived && row.dateSent) {
            const received = new Date(row.dateReceived);
            const startedSending = new Date(row.dateStartedSending);
            const diffMs = startedSending.getTime() - received.getTime();
            if (!isNaN(diffMs) && diffMs >= 0) {
              const seconds = diffMs / 1000;
              return `${seconds.toFixed(3)}s`;
            }
          }
          return "-";
        },
      },
      {
        key: "lastModified",
        header: "Delay Submit Response",
        transform: async (row) => {
          if (row.dateSent && row.dateStartedSending) {
            const startedSending = new Date(row.dateStartedSending);
            const sent = new Date(row.dateSent);
            const diffMs = sent.getTime() - startedSending.getTime();
            if (!isNaN(diffMs) && diffMs >= 0) {
              const seconds = diffMs / 1000;
              return `${seconds.toFixed(3)}s`;
            }
          }
          return "-";
        },
      },
      {
        key: "dateDlrReceivedFromVendor",
        header: "DLR Delay From Vendor",
        transform: async (row) => {
          if (row.dateSent && row.dateDlrReceivedFromVendor) {
            const sent = new Date(row.dateSent);
            const dlrFromVendor = new Date(row.dateDlrReceivedFromVendor);
            const diffMs = dlrFromVendor.getTime() - sent.getTime();
            if (!isNaN(diffMs) && diffMs >= 0) {
              const seconds = diffMs / 1000;
              return `${seconds.toFixed(3)}s`;
            }
          }
          return "-";
        },
      },
      {
        key: "dateDlrStartedSendingToClient",
        header: "DLR Delay to Client",
        transform: async (row) => {
          if (row.dateDlrReceivedFromVendor) {
            const dlrFromVendor = new Date(row.dateDlrReceivedFromVendor);
            // old CDRs have no startedSendingToClient -> treat as fromVendor (internal delay 0)
            const started = new Date(
              row.dateDlrStartedSendingToClient ??
                row.dateDlrReceivedFromVendor,
            );
            const diffMs = started.getTime() - dlrFromVendor.getTime();
            if (!isNaN(diffMs) && diffMs >= 0) {
              const seconds = diffMs / 1000;
              return `${seconds.toFixed(3)}s`;
            }
          }
          return "-";
        },
      },
      {
        key: "dateDlrRespReceivedFromClient",
        header: "DLR Delay from Client",
        transform: async (row) => {
          if (
            row.dateDlrRespReceivedFromClient &&
            row.dateDlrReceivedFromVendor
          ) {
            // same fallback: old CDRs get the full old delay attributed to the client
            const started = new Date(
              row.dateDlrStartedSendingToClient ??
                row.dateDlrReceivedFromVendor,
            );
            const resp = new Date(row.dateDlrRespReceivedFromClient);
            const diffMs = resp.getTime() - started.getTime();
            if (!isNaN(diffMs) && diffMs >= 0) {
              const seconds = diffMs / 1000;
              return `${seconds.toFixed(3)}s`;
            }
          }
          return "-";
        },
      },
      { key: "stage", header: "Status Reason" },
      { key: "failureCause", header: "Failure Cause" },
      {
        key: "lastModified",
        header: "DLR Error Code",
        transform: async () => "-", // TODO: Add when backend provides this field
      },
    ],
    [queryClient],
  );

  const headers = fields.map((f) => f.header);

  useEffect(() => {
    if (open) {
      setPageInput({ page: 0, size: 20 });
    }
  }, [open]);

  useEffect(() => {
    setHighlightedRowId(null);
  }, [pageable.content]);

  const handleRowPress = (
    e: React.MouseEvent,
    rowId: string | number,
  ) => {
    const target = e.target as HTMLElement;
    if (
      target.closest(
        "button, a, input, textarea, select, [role='button'], [role='checkbox']",
      )
    )
      return;
    setHighlightedRowId((prev) => (prev === rowId ? null : rowId));
  };

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        setLoading(true);
        let operatorId = extendedParams.destinationOperatorId;
        if (!operatorId && (extendedParams as any).operatorName) {
          const resolvedId = await getOperatorIdByName(
            (extendedParams as any).operatorName,
          );
          operatorId = resolvedId === null ? undefined : resolvedId;
        }
        const numericOperatorId =
          typeof operatorId === "string"
            ? isNaN(Number(operatorId))
              ? undefined
              : Number(operatorId)
            : operatorId;

        const isNegativeVendorId =
          typeof extendedParams.vendorId === "number" &&
          extendedParams.vendorId < 0;

        const completeParams: any = {
          ...params,
          vendorId: extendedParams.vendorId,
          destinationOperatorId: numericOperatorId,
          connectionProfileId: extendedParams.connectionProfileId,
          page: pageInput.page,
          size: pageInput.size,
        };

        if (isNegativeVendorId) {
          delete completeParams.routingCost;
          delete completeParams.convertedRoutingCost;
        }

        const result = await fetchMessageTrackersDialog(completeParams);
        setPageable({
          content: result.content || [],
          totalElements: result.totalElements || 0,
          totalPages: result.totalPages || 0,
          number: result.number || 0,
          size: result.size || pageInput.size,
        });
      } catch (err) {
        console.error("Error fetching message trackers:", err);
        setPageable({
          content: [],
          totalElements: 0,
          totalPages: 0,
          number: 0,
          size: pageInput.size,
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [open, params, pageInput.page, pageInput.size, extendedParams]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      fullScreen={isSmallScreen}
      PaperProps={{
        sx: isSmallScreen
          ? { width: "100vw", maxWidth: "100vw", margin: 0, borderRadius: 0 }
          : { maxWidth: "1400px" },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          pr: 7,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box component="span">Message Trackers</Box>
        {!loading && pageable.content.length > 0 && (
          <CustomButton
            size="small"
            bgColor={COLORS.GOLD}
            startIcon={<Export />}
            onClick={() => setCdrExportDialogOpen(true)}
            textColor={COLORS.iconColor}
            label="Export"
            permission={
              usedBy === "live-monitor"
                ? "GET_ALL_CDRS_LIVE_MONITOR_CSV"
                : "GET_ALL_CDRS_REPRICE_CSV"
            }
          />
        )}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <CircularProgress />
        ) : pageable.content.length ? (
          <>
            <TableContainer
              ref={dragScrollRef}
              sx={{
                maxHeight: { xs: "calc(100vh - 170px)", sm: "60vh" },
                "@supports (height: 100dvh)": {
                  maxHeight: { xs: "calc(100dvh - 170px)", sm: "60vh" },
                },
                "& .MuiTableCell-stickyHeader": { backgroundColor: "#fff" },
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {headers.map((header, idx) => (
                      <TableCell
                        key={idx}
                        sx={{
                          fontWeight: "bold",
                          minWidth: "200px",
                          textAlign: "center",
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageable.content.map((item, rowIndex) => (
                    <TableRow
                      key={item.id}
                      onClick={(e) => handleRowPress(e, item.id)}
                      sx={{
                        cursor: "pointer",
                        backgroundColor:
                          rowIndex % 2 === 0 ? "#f5f5f5" : "#ffffff",
                        "&:hover": { filter: "brightness(0.98)" },
                        ...(highlightedRowId === item.id && {
                          backgroundColor: "rgba(163, 148, 85, 0.22)",
                          boxShadow: "inset 3px 0 0 0 #A39455",
                        }),
                      }}
                    >
                      {fields.map((field, idx) =>
                        field.transform ? (
                          <TransformTableCell
                            key={idx}
                            field={field}
                            item={item}
                          />
                        ) : field.options ? (
                          <QueriedTableCell
                            key={idx}
                            options={field.options(item)}
                          />
                        ) : (
                          <TableCell
                            key={idx}
                            sx={{
                              minWidth: "200px",
                              textAlign: "center",
                              borderBottom: "none",
                            }}
                          >
                            {formatValue(item[field.key])}
                          </TableCell>
                        ),
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <PaginationRow
              pageInput={pageInput}
              setPageInput={setPageInput}
              totalElements={pageable.totalElements}
              totalPages={pageable.totalPages}
            />
          </>
        ) : (
          <Typography>No data available.</Typography>
        )}
      </DialogContent>
      <CdrExportDialog
        open={cdrExportDialogOpen}
        onClose={() => setCdrExportDialogOpen(false)}
        exportParams={params}
        cdrCount={pageable.totalElements}
      />
    </Dialog>
  );
};

interface QueriedTableCellProps {
  options: UseQueryOptions<any, any>;
}

const QueriedTableCell: React.FC<QueriedTableCellProps> = ({ options }) => {
  const query = useQuery(options);

  const getDisplayValue = (data: unknown): string => {
    if (data == null) return "-";
    if (typeof data === "string" || typeof data === "number") {
      return String(data);
    }
    if (typeof data === "object") {
      const keys = Object.keys(data);
      if (keys.length === 1) {
        return String((data as Record<string, any>)[keys[0]]);
      }
      return JSON.stringify(data, null, 2);
    }
    return "-";
  };

  if (query.isLoading) {
    return (
      <TableCell sx={{ textAlign: "center", minWidth: "150px" }}>
        <CircularProgress size={24} sx={{ color: COLORS.GOLD }} />
      </TableCell>
    );
  }

  if (query.isError) {
    return (
      <TableCell
        sx={{
          textAlign: "center",
          color: COLORS.RED,
          minWidth: "150px",
        }}
      >
        <WarningAmberIcon />
      </TableCell>
    );
  }

  return (
    <TableCell sx={{ minWidth: "200px", borderBottom: "none" }}>
      <Typography sx={{ textAlign: "center", fontSize: "inherit" }}>
        {getDisplayValue(query.data)}
      </Typography>
    </TableCell>
  );
};

interface TransformTableCellProps {
  field: IField<MessageTrackerType>;
  item: MessageTrackerType;
}

const TransformTableCell: React.FC<TransformTableCellProps> = ({
  field,
  item,
}) => {
  const [transformedValue, setTransformedValue] = useState<any>("-");
  const [loading, setLoading] = useState(true);

  // Memoize here too like memoize in LiveMonitor
  const promise = useMemo(
    () => (field.transform ? field.transform(item) : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item, field],
  );

  useEffect(() => {
    if (promise === undefined) {
      setTransformedValue(formatValue(item[field.key]));
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.resolve(promise)
      .then((result) => setTransformedValue(result))
      .catch((error) => {
        console.error("Transform error:", error);
        setTransformedValue("-");
      })
      .finally(() => setLoading(false));
  }, [promise, field.key, item]);

  if (loading) {
    return (
      <TableCell sx={{ textAlign: "center", minWidth: "200px" }}>
        <CircularProgress size={24} sx={{ color: COLORS.GOLD }} />
      </TableCell>
    );
  }

  return (
    <TableCell
      sx={{
        minWidth: "200px",
        textAlign: "center",
        borderBottom: "none",
      }}
    >
      {React.isValidElement(transformedValue) ? (
        transformedValue
      ) : (
        <Typography sx={{ textAlign: "center", fontSize: "inherit" }}>
          {String(transformedValue)}
        </Typography>
      )}
    </TableCell>
  );
};
interface PaginationRowProps {
  pageInput: { page: number; size: number };
  setPageInput: React.Dispatch<
    React.SetStateAction<{ page: number; size: number }>
  >;
  totalElements: number;
  totalPages: number;
}

function PaginationRow({
  pageInput,
  setPageInput,
  totalElements,
  totalPages,
}: PaginationRowProps) {
  const currentPage = pageInput.page;
  const [pageInputValue, setPageInputValue] = useState<string>(
    String(currentPage + 1),
  );

  useEffect(() => {
    setPageInputValue(String(currentPage + 1));
  }, [currentPage]);

  const handlePageInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    if (value === "" || /^[0-9\b]+$/.test(value)) {
      setPageInputValue(value);
    }
  };

  const applyPageChange = () => {
    let pageNumber = parseInt(pageInputValue, 10);
    if (!isNaN(pageNumber) && pageNumber > 0 && pageNumber <= totalPages) {
      setPageInput((prev) => ({ ...prev, page: pageNumber - 1 }));
    } else {
      setPageInputValue(String(currentPage + 1));
    }
  };

  const handlePageInputKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Enter") {
      applyPageChange();
      (event.target as HTMLInputElement).blur();
    }
  };

  const handlePageInputBlur = () => {
    applyPageChange();
  };
  return (
    <Box
      display="flex"
      alignItems="center"
      flexWrap="wrap"
      gap={{ xs: 1, sm: 2 }}
      ml={{ xs: 0, sm: 1 }}
      mr={{ xs: 0, sm: 1 }}
      sx={{ width: "100%", p: { xs: "4px 8px", sm: 0 } }}
    >
      <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }}>
        <IconButton
          disabled={currentPage <= 0}
          onClick={() =>
            setPageInput((prev) => ({
              ...prev,
              page: Math.max(prev.page - 1, 0),
            }))
          }
          sx={{
            color: currentPage > 0 ? COLORS.placeHolderColor : "#ccc",
            fontSize: { xs: "16px", sm: "18px" },
            p: { xs: 0.5, sm: 1 },
          }}
        >
          <ArrowBackIosNew fontSize="small" />
        </IconButton>

        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          sx={{
            fontWeight: "bold",
            fontSize: { xs: "13px", sm: "16px" },
            color: COLORS.placeHolderColor,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "13px", sm: "16px" },
              fontWeight: "bold",
              color: COLORS.placeHolderColor,
            }}
          >
            Page
          </Typography>
          <TextField
            value={pageInputValue}
            onChange={handlePageInputChange}
            onKeyPress={handlePageInputKeyPress}
            onBlur={handlePageInputBlur}
            size="small"
            sx={{
              width: { xs: "40px", sm: "50px" },
              "& .MuiInputBase-root": {
                height: { xs: "24px", sm: "28px" },
                fontSize: { xs: "12px", sm: "14px" },
              },
              "& .MuiInputBase-input": {
                textAlign: "center",
                padding: { xs: "2px 4px", sm: "4px 6px" },
                fontWeight: "bold",
                color: COLORS.placeHolderColor,
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#e0e0e0",
                },
                "&:hover fieldset": {
                  borderColor: COLORS.placeHolderColor,
                },
                "&.Mui-focused fieldset": {
                  borderColor: COLORS.placeHolderColor,
                  borderWidth: "1px",
                },
              },
            }}
            inputProps={{
              min: 1,
              max: totalPages,
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
          />
          <Typography
            sx={{
              fontSize: { xs: "13px", sm: "16px" },
              fontWeight: "bold",
              color: COLORS.placeHolderColor,
            }}
          >
            of {totalPages}
          </Typography>
        </Box>

        <IconButton
          disabled={currentPage + 1 >= totalPages}
          onClick={() =>
            setPageInput((prev) => ({
              ...prev,
              page: Math.min(prev.page + 1, totalPages - 1),
            }))
          }
          sx={{
            color:
              currentPage + 1 < totalPages ? COLORS.placeHolderColor : "#ccc",
            fontSize: { xs: "16px", sm: "18px" },
            p: { xs: 0.5, sm: 1 },
          }}
        >
          <ArrowForwardIos fontSize="small" />
        </IconButton>
      </Box>

      <Box
        display="flex"
        alignItems="center"
        gap={{ xs: 0.5, sm: 1 }}
        sx={{ flexShrink: 0 }}
      >
        <Box
          sx={{
            fontSize: { xs: "11px", sm: "0.875rem" },
            color: COLORS.placeHolderColor,
            display: { xs: "none", sm: "block" },
          }}
        >
          Rows per page:
        </Box>
        <Box
          sx={{
            fontSize: "11px",
            color: COLORS.placeHolderColor,
            display: { xs: "block", sm: "none" },
          }}
        >
          Rows:
        </Box>
        <Select
          value={pageInput.size}
          onChange={(e) =>
            setPageInput((prev) => ({
              ...prev,
              size: Number(e.target.value),
              page: 0,
            }))
          }
          sx={{
            height: { xs: "24px", sm: "32px" },
            fontSize: { xs: "11px", sm: "0.875rem" },
            width: { xs: "58px", sm: "80px" },
            minWidth: { xs: "58px", sm: "80px" },
            "& .MuiSelect-select": {
              padding: { xs: "2px 4px", sm: "4px 8px" },
            },
          }}
        >
          <MenuItem value={10}>10</MenuItem>
          <MenuItem value={20}>20</MenuItem>
          <MenuItem value={50}>50</MenuItem>
          <MenuItem value={100}>100</MenuItem>
        </Select>
      </Box>
      <Typography
        sx={{
          fontSize: { xs: "11px", sm: "0.875rem" },
          color: COLORS.placeHolderColor,
          ml: "auto",
          whiteSpace: "nowrap",
        }}
      >
        Total elements: {totalElements}
      </Typography>
    </Box>
  );
}
